import { Injectable, Injector } from "@angular/core";
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { plainToClass } from "class-transformer";
import * as _ from "lodash";
import { BehaviorSubject, lastValueFrom } from "rxjs";
import { API_ASSOCIATIONS } from "src/common/constants/api.constants";
import { PermissionEnum } from "src/common/constants/auth.constants";
import { TAPIListResult } from "src/common/contracts/api.contracts";
import { environment } from "src/environments/environment";
import { apiCallWrapper } from "../../api/api.util";
import { HasIdOrExternalValueOrExternalIdOrServiceName } from "../../api/generic.api";
import { UserApi } from "../../api/user/user.api";
import { IQueryFilter } from "../../model/query.filter.class";
import { User } from "../../model/user/user.model";
import { BaseService } from "../base.service";
import { ToastService } from "../toast.service";
import { WSUserService } from "../web-socket/ws-user.service";
import { UserImpersonateService } from "./user-impersonate.service";

@Injectable({
  providedIn: "root",
})
/**
 * @description This service is used to manage user API.
 */
export class UserService extends BaseService {
  public currentUserSubject = new BehaviorSubject<User | null>(null);
  public impersonatingUserSubject = new BehaviorSubject<User | null>(null);

  constructor(
    private toastService: ToastService,
    private userApi: UserApi,
    private userImpersonateService: UserImpersonateService,
    private router: Router,
    private injector: Injector
  ) {
    super();
  }

  public clearUser(clearImpersonation: boolean = false): void {
    this.currentUserSubject.next(null);
    if (clearImpersonation) {
      this.impersonatingUserSubject.next(null);
    }
    this.injector.get(WSUserService).unregisterWSUser();
  }

  /**
   * @description This method is used to get the user by id and set it in the currentUserSubject.
   * @param id
   * @returns
   */
  public async setUser(
    id: string,
    checkForCurrentImpersonation: boolean = false,
    setSentry: boolean = false
  ): Promise<boolean> {
    try {
      const query: IQueryFilter = new IQueryFilter(_.cloneDeep(API_ASSOCIATIONS.user));
      query.filter = { id: id };

      const data = await lastValueFrom(
        apiCallWrapper(this.userApi.list(query), {
          toastService: this.toastService,
          action: "Obtaining user details",
        })
      );

      if (data && data.rows && data.rows.length > 0) {
        let user: User = plainToClass(User, data.rows[0]);

        if (checkForCurrentImpersonation) {
          // Check if we are currently impersonating.  If the user has refreshed the page while impersonating,
          // we need to set the impersonation state correctly.
          if (this.userImpersonateService.isImpersonating) {
            const impersonatedUser = await this.userImpersonateService.getCurrentImpersonationUser(
              user.uuid
            );
            if (impersonatedUser) {
              this.currentUserSubject.next(user);
              this._setImpersonatedUser(impersonatedUser);
            } else {
              // failed to get impersonated user, clear the flag
              this.userImpersonateService.clearImpersonationFlag();
            }
          } else {
            this.currentUserSubject.next(user);
          }
        } else {
          this.currentUserSubject.next(user);
        }

        if (setSentry) {
          //TODO: tried to put this in a SentryService but it didn't work
          Sentry.setUser({
            id: user.id,
            email: user.email,
          });
        }
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  private async _setImpersonatedUser(userBeingImpersonatedId: User): Promise<boolean> {
    this.impersonatingUserSubject.next(this.currentUser);
    return await this.setUser(userBeingImpersonatedId.id);
  }

  public clearImpersonatedUser(): void {
    if (this.impersonatingUserSubject.value) {
      this.currentUserSubject.next(this.impersonatingUserSubject.value);
      this.impersonatingUserSubject.next(null);
    }
  }

  public get currentUser(): User | null {
    return this.currentUserSubject.value;
  }

  public get impersonatingUser(): User | null {
    return this.impersonatingUserSubject.value;
  }

  public async getUsersByCurrentUserId(): Promise<TAPIListResult<User>> {
    try {
      const query: IQueryFilter = new IQueryFilter(_.cloneDeep(API_ASSOCIATIONS.user));

      const data = await lastValueFrom(
        apiCallWrapper(this.userApi.getUsersByCurrentUserId(query), {
          toastService: this.toastService,
          action: "Obtaining user details",
        })
      );

      return this.inflateModel<User, User & HasIdOrExternalValueOrExternalIdOrServiceName<string>>(
        User,
        data
      );
    } catch (error) {
      throw error;
    }
  }

  // Added method to get users by deal
  public async getUsersByDeal(dealExternalId: string): Promise<User[]> {
    try {
      const response = await lastValueFrom(
        apiCallWrapper(this.userApi.getUsersByDeal(dealExternalId), {
          toastService: this.toastService,
          action: "Fetching users for deal",
        })
      );

      const rows = response?.rows ?? [];
      return rows.map((u: any) => plainToClass(User, u));
    } catch {
      return [];
    }
  }

  public async getUserByUUID(userUuid: string): Promise<User> {
    try {
      const response = await lastValueFrom(
        apiCallWrapper(this.userApi.getUserByUUID(userUuid), {
          toastService: this.toastService,
          action: "Fetching user",
        })
      );

      return plainToClass(User, response);
    } catch (error) {
      throw error;
    }
  }

  public async impersonateUser(
    currentUserUUID: string,
    userToImpersonateUUID: string,
    impersonationReason: string
  ): Promise<void> {
    try {
      const impersonatedUser = await this.userImpersonateService.startImpersonation(
        currentUserUUID,
        userToImpersonateUUID,
        impersonationReason
      );

      if (!impersonatedUser) {
        throw new Error("Impersonation failed");
      }

      // swap current user to impersonated user
      await this._setImpersonatedUser(impersonatedUser);

      this.toastService.showSuccess("Impersonation started successfully");
      // Redirect to home page after impersonation starts
      this.router.navigateByUrl(this.defaultLandingPage);
    } catch (error) {
      this.toastService.showError("Error starting impersonation");
    }
  }

  public async stopImpersonation(currentUserUUID: string): Promise<void> {
    try {
      const result = await this.userImpersonateService.stopImpersonation(currentUserUUID);

      if (!result) {
        throw new Error("Stop impersonation failed");
      }

      // swap back to original user
      this.clearImpersonatedUser();

      this.toastService.showSuccess("Impersonation ended. You are now yourself again.");
      this.router.navigateByUrl(this.defaultLandingPage);
    } catch (error) {
      this.toastService.showError("Failed to stop impersonation.");
    }
  }

  public get defaultLandingPage(): string {
    if (!this.currentUser || !this.currentUser.userRoles.length) {
      return "/login";
    }
    if (this.currentUser.isCollaborationOnly) return "/dashboard/collaboration";
    if (this.currentUser.isAdmin) {
      return "/admin-dashboard";
    } else if (this.currentUser.isDRO) {
      return this.currentUser.hasPermissions([PermissionEnum.ScreenBenchmarkRead]) || this.currentUser.hasPermissions([PermissionEnum.ScreenBenchmarkWrite]) ? "/dro-dashboard" : "/dashboard/collaboration";
    } else if (this.currentUser.isOperations) {
      return this.currentUser.hasPermissions([PermissionEnum.ScreenInternalDashboardRead]) ? "/dashboard" : "/dashboard/collaboration";
    } else if (this.currentUser.isClient) {
      if (
        environment.featureFlags.userAccessControl &&
        this.currentUser.hasPermissions([PermissionEnum.ScreenViewDealList])
      ) {
        return "/dashboard/deal-list";
      } else if (
        this.currentUser.hasPermissions([PermissionEnum.ScreenClientDashboardRead]) ||
        this.currentUser.hasPermissions([PermissionEnum.ScreenClientDashboardWrite])
      ) {
        return "/client-dashboard";
      } else {
        // Existing Client accounts may have only product-scoped collaboration access.
        // Do not send them to a dashboard whose guard redirects back to this landing page.
        // The collaboration catalog and every product operation remain server-authorised.
        return "/dashboard/collaboration";
      }
    } else {
      return "/login";
    }
  }
}
